import { useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowLeft,
  ChevronDown,
  MoreHorizontal,
  Pencil,
  RotateCcw,
  Scale,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import { incidentContext, useLoupeContextLines } from "../../lib/loupe-context";
import { cn } from "../../lib/utils";
import { scrollBehavior } from "../../lib/motion";
import { notifyApiError, type ErrorMessages } from "../../lib/toast-error";
import { formatDateTime } from "../../lib/format";
import { InitialsAvatar } from "../../components/initials-avatar";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  useAddTimelineEvent,
  useChangeStatus,
  useClearLegalHold,
  useIncident,
  useReopenIncident,
  useSetAssignees,
  useSetLegalHold,
  useSetOwner,
  useSetTags,
  useTakeIncident,
  useTimeline,
  type IncidentStatus,
  type ManualTimelineType,
} from "./api";
import {
  buildReopenRequest,
  isReopenActionVisible,
  isReopenReasonValid,
  MIN_REOPEN_REASON_CHARS,
  showsReopenEscalation,
} from "./reopen";
import {
  buildSetLegalHoldRequest,
  isLegalHoldActionVisible,
  isLegalHoldReasonValid,
  MIN_LEGAL_HOLD_REASON_CHARS,
} from "./legal-hold";
import { activeOnly, useMasterData } from "../master-data/api";
import { activeUsers, toUserMap, useUsers, userName } from "../users/api";
import { usePostmortem } from "../postmortem/api";
import { AttachmentsCard } from "../attachments/attachments-card";
import { IncidentOperationsLogCard } from "../operations-log/incident-operations-log-card";
import { isDraft, missingMandatorySections } from "../postmortem/completeness";
import { canEditPostmortem } from "../postmortem/permissions";
import { downloadIncidentPdf } from "../export/api";
import { ExportButton } from "../export/export-button";
import { canExport } from "../export/permissions";
import { IncidentEditDialog } from "./incident-edit-dialog";
import { lastListSearch } from "./list-view";
import {
  canAssign,
  canChangeStatus,
  canComment,
  canEdit,
  canEditTags,
  canReassignRetiredOwner,
  canTake,
  canWriteTimelineEntry,
} from "./permissions";
import { isIncidentParty } from "../../lib/access";
import { StatusBadge } from "./components/status-badge";
import { ArchivedBadge } from "./components/archived-badge";
import { SeverityLabel } from "./components/severity-label";
import {
  isManualTimelineType,
  MANUAL_TIMELINE_TYPES,
  matchesTimelineFilter,
  availableNextStatuses,
  STATUS_LABEL_KEY,
  TIMELINE_FILTER_LABEL_KEY,
  TIMELINE_FILTERS,
  TIMELINE_TYPE_LABEL_KEY,
  timelineText,
  type TimelineFilter,
} from "./status";

/**
 * Sentinel for the "no owner" option in the owner picker. Radix Select forbids
 * an empty-string item value, so the unassign choice carries this token and is
 * translated back to `null` on the way to the owner endpoint.
 */
const OWNER_NONE = "__none__";

export function IncidentDetailPage(): JSX.Element {
  const { id } = useParams({ from: "/incidents/$id" });
  const { t } = useTranslation();
  const { hasRole, user } = useAuth();

  const incidentQuery = useIncident(id);
  const timelineQuery = useTimeline(id);
  const postmortemQuery = usePostmortem(id);
  const master = useMasterData();
  const usersQuery = useUsers();
  const userById = useMemo(() => toUserMap(usersQuery.data), [usersQuery.data]);
  const changeStatus = useChangeStatus(id);
  const setOwner = useSetOwner(id);
  const takeIncident = useTakeIncident(id);
  const setAssignees = useSetAssignees(id);
  const setTags = useSetTags(id);
  const addEvent = useAddTimelineEvent(id);
  const reopenIncident = useReopenIncident(id);
  const setLegalHold = useSetLegalHold(id);
  const clearLegalHold = useClearLegalHold(id);
  // A Loupe entry written here names the incident and how this account is
  // involved in it - the "own incidents" half of the rights (loupe-context.ts).
  useLoupeContextLines(
    incidentQuery.data && user?.userId ? incidentContext(incidentQuery.data, user.userId, t) : null,
  );

  const [composerType, setComposerType] =
    useState<ManualTimelineType>("kommentar");
  const [composerText, setComposerText] = useState("");
  // Segmented client-side timeline filter (M-005): Alle / Manuell / System.
  const [timelineFilter, setTimelineFilter] = useState<TimelineFilter>("alle");
  // Mobile-only collapse state of the meta card (M-005); md+ ignores it.
  const [metaOpen, setMetaOpen] = useState(false);
  // Zuordnung card anchor for the "Owner zuweisen" callout action (M-005).
  const assignmentRef = useRef<HTMLDivElement | null>(null);
  // Draft assignee selection; null means "in sync with the loaded incident".
  const [assigneeDraft, setAssigneeDraft] = useState<string[] | null>(null);
  // Reopen dialog state (F-018): open flag + Begruendung + Admin-Korrektur.
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [reopenAdminCorrection, setReopenAdminCorrection] = useState(false);
  // Legal-hold dialogs (GR-004): set (with Begruendung) and clear (confirm).
  const [legalHoldSetOpen, setLegalHoldSetOpen] = useState(false);
  const [legalHoldReason, setLegalHoldReason] = useState("");
  const [legalHoldClearOpen, setLegalHoldClearOpen] = useState(false);
  // `incident.edit` dialog: title, description, category, severity, areas.
  const [editOpen, setEditOpen] = useState(false);

  // GR-007's Admin-Korrektur is a variant of the reopen, claimed by the admin
  // role only; the server re-checks it.
  const isAdmin = hasRole("admin");

  const errorMessages: ErrorMessages = {
    precondition: t("errors.precondition"),
    forbidden: t("errors.forbidden"),
    conflict: t("errors.conflict"),
    generic: t("errors.generic"),
  };

  if (incidentQuery.isLoading) {
    return <DetailSkeleton />;
  }

  if (incidentQuery.isError || !incidentQuery.data) {
    return (
      <div
        role="alert"
        data-testid="incident-detail_load-error"
        className="flex items-center justify-between gap-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm"
      >
        <span>{t("errors.load")}</span>
        <Button
          variant="outline"
          size="sm"
          data-testid="incident-detail_retry"
          onClick={() => void incidentQuery.refetch()}
        >
          {t("common.retry")}
        </Button>
      </div>
    );
  }

  const incident = incidentQuery.data;
  const nextStatuses = availableNextStatuses(incident.status, incident.ownerId);
  // What this viewer may do here is the incident's `can` ([F-027]), decided by
  // the server; the controls below only follow it.
  const mayChangeStatus = canChangeStatus(incident);
  const mayEdit = canEdit(incident);
  const mayEditTags = canEditTags(incident);
  const mayComment = canComment(incident);
  const mayWriteEntry = canWriteTimelineEntry(incident);
  const mayTake = canTake(incident);
  const mayAssign = canAssign(incident);
  const ownerRetired = incident.ownerId !== null && !userById.get(incident.ownerId)?.active;
  const mayNameOwner = mayAssign || canReassignRetiredOwner(incident, ownerRetired);
  const assignmentPending = setOwner.isPending || takeIncident.isPending || setAssignees.isPending;
  // Reopen action (F-018/GR-007): only on a CLOSED incident whose `can` holds it.
  const mayReopen = isReopenActionVisible(incident.status, incident);
  const reopenReasonChars = reopenReason.trim().length;
  // Legal-hold controls (GR-004); the badge shows for everyone.
  const mayManageLegalHold = isLegalHoldActionVisible(incident);
  const legalHoldReasonChars = legalHoldReason.trim().length;

  const areaNames = incident.areaIds
    .map((areaId) => master.areaById.get(areaId)?.name)
    .filter((name): name is string => Boolean(name));
  const tagNames = incident.tagIds
    .map((tagId) => master.tagById.get(tagId)?.name)
    .filter((name): name is string => Boolean(name));
  const assigneeNames = incident.assigneeIds.map((uid) =>
    userName(uid, userById),
  );

  // Assignee editor: fall back to the loaded set until the user edits (draft).
  const currentAssignees = assigneeDraft ?? incident.assigneeIds;
  const assigneesDirty =
    assigneeDraft !== null &&
    (assigneeDraft.length !== incident.assigneeIds.length ||
      assigneeDraft.some((uid) => !incident.assigneeIds.includes(uid)));

  const onChangeStatus = (value: string): void => {
    changeStatus.mutate(
      { toStatus: value as IncidentStatus, version: incident.version },
      {
        onSuccess: () => toast.success(t("detail.statusChanged")),
        onError: (error) =>
          notifyApiError(error, errorMessages, () =>
            void incidentQuery.refetch(),
          ),
      },
    );
  };

  const onChangeOwner = (value: string): void => {
    const ownerId = value === OWNER_NONE ? null : value;
    setOwner.mutate(
      { ownerId, version: incident.version },
      {
        onSuccess: () => toast.success(t("detail.ownerAssigned")),
        onError: (error) =>
          notifyApiError(error, errorMessages, () =>
            void incidentQuery.refetch(),
          ),
      },
    );
  };

  const onTake = (): void => {
    takeIncident.mutate(
      { version: incident.version },
      {
        onSuccess: () => toast.success(t("detail.taken")),
        onError: (error) =>
          notifyApiError(error, errorMessages, () =>
            void incidentQuery.refetch(),
          ),
      },
    );
  };

  // Tag chips (M-005): the tag set goes to its own endpoint (`incident.tags`).
  const onChangeTags = (tagIds: string[]): void => {
    setTags.mutate(
      { tagIds, version: incident.version },
      {
        onSuccess: () => toast.success(t("detail.tagsUpdated")),
        onError: (error) =>
          notifyApiError(error, errorMessages, () =>
            void incidentQuery.refetch(),
          ),
      },
    );
  };

  const focusAssignment = (): void => {
    assignmentRef.current?.scrollIntoView({
      behavior: scrollBehavior(),
      block: "start",
    });
    assignmentRef.current?.focus({ preventScroll: true });
  };

  const onToggleAssignee = (userId: string, checked: boolean): void => {
    const base = assigneeDraft ?? incident.assigneeIds;
    setAssigneeDraft(
      checked ? [...base, userId] : base.filter((uid) => uid !== userId),
    );
  };

  const onSaveAssignees = (): void => {
    if (assigneeDraft === null) return;
    setAssignees.mutate(
      { assigneeIds: assigneeDraft, version: incident.version },
      {
        onSuccess: () => {
          setAssigneeDraft(null);
          toast.success(t("detail.assigneesSaved"));
        },
        onError: (error) =>
          notifyApiError(error, errorMessages, () => {
            setAssigneeDraft(null);
            void incidentQuery.refetch();
          }),
      },
    );
  };

  const closeReopenDialog = (): void => {
    setReopenOpen(false);
    setReopenReason("");
    setReopenAdminCorrection(false);
  };

  const onConfirmReopen = (): void => {
    if (!isReopenReasonValid(reopenReason)) return;
    reopenIncident.mutate(
      {
        body: buildReopenRequest(reopenReason, reopenAdminCorrection, isAdmin),
        version: incident.version,
      },
      {
        onSuccess: () => {
          closeReopenDialog();
          toast.success(t("reopen.done"));
        },
        onError: (error) => {
          closeReopenDialog();
          notifyApiError(error, errorMessages, () =>
            void incidentQuery.refetch(),
          );
        },
      },
    );
  };

  const closeLegalHoldSetDialog = (): void => {
    setLegalHoldSetOpen(false);
    setLegalHoldReason("");
  };

  const onConfirmSetLegalHold = (): void => {
    if (!isLegalHoldReasonValid(legalHoldReason)) return;
    setLegalHold.mutate(
      {
        body: buildSetLegalHoldRequest(legalHoldReason),
        version: incident.version,
      },
      {
        onSuccess: () => {
          closeLegalHoldSetDialog();
          toast.success(t("legalHold.setDone"));
        },
        onError: (error) => {
          closeLegalHoldSetDialog();
          notifyApiError(error, errorMessages, () =>
            void incidentQuery.refetch(),
          );
        },
      },
    );
  };

  const onConfirmClearLegalHold = (): void => {
    clearLegalHold.mutate(
      { version: incident.version },
      {
        onSuccess: () => {
          setLegalHoldClearOpen(false);
          toast.success(t("legalHold.clearDone"));
        },
        onError: (error) => {
          setLegalHoldClearOpen(false);
          notifyApiError(error, errorMessages, () =>
            void incidentQuery.refetch(),
          );
        },
      },
    );
  };

  const onAddEntry = (): void => {
    const text = composerText.trim();
    if (!text) return;
    const type: ManualTimelineType = mayWriteEntry ? composerType : "kommentar";
    addEvent.mutate(
      { type, text },
      {
        onSuccess: () => {
          setComposerText("");
          toast.success(t("detail.entryAdded"));
        },
        onError: (error) => notifyApiError(error, errorMessages),
      },
    );
  };

  const timelineItems = timelineQuery.data?.items ?? [];
  const visibleTimelineItems = timelineItems.filter((event) =>
    matchesTimelineFilter(timelineFilter, event.type),
  );

  // Master-data tags still assignable (active, not yet applied).
  const addableTags = activeOnly(master.tags.data).filter(
    (tag) => !incident.tagIds.includes(tag.id),
  );

  // Meta rows are rendered twice (M-005): in the collapsible below-lg card and
  // in the lg+ sidebar card. Shared JSX, not a component - no own state.
  const metaRows = (sidebar: boolean): JSX.Element => (
    <>
      <MetaRow label={t("detail.severity")} testid={sidebar ? "incident-detail_severity-sidebar" : "incident-detail_severity-inline"}>
        <SeverityLabel severity={master.severityById.get(incident.severityId)} />
      </MetaRow>
      <MetaRow label={t("detail.category")} testid={sidebar ? "incident-detail_category-sidebar" : "incident-detail_category-inline"}>
        {master.categoryById.get(incident.categoryId)?.name ??
          t("common.none")}
      </MetaRow>
      <MetaRow label={t("detail.areas")} testid={sidebar ? "incident-detail_areas-sidebar" : "incident-detail_areas-inline"}>
        {areaNames.length > 0 ? areaNames.join(", ") : t("common.none")}
      </MetaRow>
      <MetaRow label={t("detail.owner")} testid={sidebar ? "incident-detail_owner-sidebar" : "incident-detail_owner-inline"}>
        {incident.ownerId
          ? userName(incident.ownerId, userById)
          : t("detail.notAssigned")}
      </MetaRow>
      <MetaRow label={t("detail.assignees")} testid={sidebar ? "incident-detail_assignees-sidebar" : "incident-detail_assignees-inline"}>
        {assigneeNames.length > 0
          ? assigneeNames.join(", ")
          : t("detail.notAssigned")}
      </MetaRow>
      <MetaRow label={t("detail.reporter")} testid={sidebar ? "incident-detail_reporter-sidebar" : "incident-detail_reporter-inline"}>
        {userName(incident.reportedById, userById)}
      </MetaRow>
      <MetaRow label={t("detail.discoveredAt")} testid={sidebar ? "incident-detail_discovered-at-sidebar" : "incident-detail_discovered-at-inline"}>
        {formatDateTime(incident.discoveredAt)}
      </MetaRow>
      <MetaRow label={t("detail.detectedVia")} testid={sidebar ? "incident-detail_detected-via-sidebar" : "incident-detail_detected-via-inline"}>
        {incident.detectedVia || t("common.none")}
      </MetaRow>
      <MetaRow label={t("detail.createdAt")} testid={sidebar ? "incident-detail_created-at-sidebar" : "incident-detail_created-at-inline"}>
        {formatDateTime(incident.createdAt)}
      </MetaRow>
      <MetaRow label={t("detail.closedAt")} testid={sidebar ? "incident-detail_closed-at-sidebar" : "incident-detail_closed-at-inline"}>
        {formatDateTime(incident.closedAt)}
      </MetaRow>
      {/* Tags (M-005): with `incident.tags` the set is edited in place -
          remove per chip, add via a compact master-data picker; read-only
          chips otherwise. */}
      {mayEditTags ? (
        <MetaRow label={t("detail.tags")} testid={sidebar ? "incident-detail_tags-sidebar" : "incident-detail_tags-inline"}>
          <div className="flex flex-wrap items-center gap-1.5">
            {incident.tagIds.map((tagId) => {
              const name = master.tagById.get(tagId)?.name ?? tagId;
              return (
                <Badge
                  key={tagId}
                  variant="secondary"
                  data-testid={sidebar ? `incident-detail_tag-sidebar_${tagId}` : `incident-detail_tag-inline_${tagId}`}
                  className="gap-1 pr-1"
                >
                  {name}
                  <button
                    type="button"
                    data-testid={sidebar ? `incident-detail_tag-remove-sidebar_${tagId}` : `incident-detail_tag-remove-inline_${tagId}`}
                    aria-label={t("detail.removeTag", { name })}
                    disabled={setTags.isPending}
                    onClick={() =>
                      onChangeTags(
                        incident.tagIds.filter((id) => id !== tagId),
                      )
                    }
                    className="rounded-full p-0.5 hover:bg-secondary-foreground/10 disabled:opacity-50"
                  >
                    <X className="h-3 w-3" aria-hidden="true" />
                  </button>
                </Badge>
              );
            })}
            {addableTags.length > 0 ? (
              <Select
                value=""
                onValueChange={(tagId) =>
                  onChangeTags([...incident.tagIds, tagId])
                }
                disabled={setTags.isPending}
              >
                <SelectTrigger
                  data-testid={sidebar ? "incident-detail_tag-picker-sidebar" : "incident-detail_tag-picker-inline"}
                  className="h-8 w-44 text-xs"
                >
                  <SelectValue placeholder={t("detail.addTag")} />
                </SelectTrigger>
                <SelectContent>
                  {addableTags.map((tag) => (
                    <SelectItem key={tag.id} value={tag.id}>
                      {tag.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
          </div>
        </MetaRow>
      ) : tagNames.length > 0 ? (
        <MetaRow label={t("detail.tags")} testid={sidebar ? "incident-detail_tags-sidebar" : "incident-detail_tags-inline"}>
          <div className="flex flex-wrap gap-1.5">
            {tagNames.map((name) => (
              <Badge key={name} variant="secondary">
                {name}
              </Badge>
            ))}
          </div>
        </MetaRow>
      ) : null}
    </>
  );

  return (
    <div className="space-y-6">
      <Link
        to="/incidents"
        search={lastListSearch()}
        data-testid="incident-detail_back"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("detail.backToList")}
      </Link>

      {/* GR-007 escalation hint (>= 2 reopens): amber block PLUS icon PLUS
          heading, mirroring the ReminderBanner (never color-alone). */}
      {showsReopenEscalation(incident.reopenCount) ? (
        <section
          role="status"
          data-testid="incident-detail_reopen-escalation"
          className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 p-4 text-amber-900 dark:text-amber-100"
        >
          <div className="flex items-center gap-2 font-semibold">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{t("reopen.escalationTitle")}</span>
          </div>
          <p className="mt-1 text-sm">
            {t("reopen.escalationHint", { count: incident.reopenCount })}
          </p>
        </section>
      ) : null}

      <Card data-testid="incident-detail_header">
        <CardHeader className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <span
                data-testid="incident-detail_number"
                className="font-mono text-sm text-muted-foreground"
              >
                {incident.number}
              </span>
              <StatusBadge status={incident.status} />
              {/* GR-004 legal-hold marker: calm slate pill, icon PLUS text
                  (never color-alone), stored Begruendung via title. Visible
                  to ALL viewers - the marker is organisational information. */}
              {incident.legalHold ? (
                <Badge
                  variant="outline"
                  data-testid="incident-detail_legal-hold-marker"
                  className="border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-900/40 text-slate-800 dark:text-slate-200"
                  title={incident.legalHoldReason ?? undefined}
                >
                  <Scale className="mr-1 h-3 w-3" aria-hidden="true" />
                  {t("legalHold.badge")}
                </Badge>
              ) : null}
              {/* GR-004: closed three years ago and archived - still fully
                  readable, only gone from the default list. */}
              {incident.archivedAt ? (
                <ArchivedBadge
                  testid="incident-detail_archived-marker"
                  archivedAt={incident.archivedAt}
                />
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* `incident.edit` ([F-001]): the incident's own fields. */}
              {mayEdit ? (
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="incident-detail_edit"
                  onClick={() => setEditOpen(true)}
                >
                  <Pencil className="h-4 w-4" aria-hidden="true" />
                  {t("detail.edit.action")}
                </Button>
              ) : null}
              {/* PDF export top-right in the header card (M-005, F-012/US-011). */}
              {canExport(incident) ? (
                <ExportButton
                  testid="incident-detail_export-pdf"
                  label={t("export.pdf")}
                  successMessage={t("export.pdfDone")}
                  download={() =>
                    downloadIncidentPdf(incident.id, `${incident.number}.pdf`)
                  }
                />
              ) : null}
              {/* Overflow menu (M-005): the secondary actions - reopen
                  (F-018) and the Admin-only legal-hold set/clear (GR-004) -
                  fold behind "...". Hidden entirely when the current user
                  has none of them. */}
              {mayReopen || mayManageLegalHold ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      data-testid="incident-detail_more-actions"
                      aria-label={t("detail.moreActions")}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {mayReopen ? (
                      <DropdownMenuItem
                        data-testid="incident-detail_reopen"
                        onSelect={() => setReopenOpen(true)}
                      >
                        <RotateCcw className="h-4 w-4" />
                        {t("reopen.action")}
                      </DropdownMenuItem>
                    ) : null}
                    {mayManageLegalHold ? (
                      incident.legalHold ? (
                        <DropdownMenuItem
                          data-testid="incident-detail_legal-hold-clear"
                          onSelect={() => setLegalHoldClearOpen(true)}
                        >
                          <Scale className="h-4 w-4" />
                          {t("legalHold.clearAction")}
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem
                          data-testid="incident-detail_legal-hold-set"
                          onSelect={() => setLegalHoldSetOpen(true)}
                        >
                          <Scale className="h-4 w-4" />
                          {t("legalHold.setAction")}
                        </DropdownMenuItem>
                      )
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
            </div>
          </div>
          <CardTitle data-testid="incident-detail_title" className="text-2xl">
            {incident.title}
          </CardTitle>
          {mayChangeStatus && nextStatuses.length > 0 ? (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Select value="" onValueChange={onChangeStatus}>
                  <SelectTrigger
                    data-testid="incident-detail_status-change"
                    className="h-11 w-full sm:h-9 sm:w-56"
                  >
                    <SelectValue placeholder={t("detail.changeStatus")} />
                  </SelectTrigger>
                  <SelectContent>
                    {nextStatuses.map((status) => (
                      <SelectItem key={status} value={status}>
                        {t(STATUS_LABEL_KEY[status])}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : null}
        </CardHeader>
      </Card>

      {mayEdit ? (
        <IncidentEditDialog
          key={`${incident.id}:${incident.version}:${editOpen}`}
          incident={incident}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onStale={() => {
            setEditOpen(false);
            void incidentQuery.refetch();
          }}
        />
      ) : null}

      {/* Missing-owner callout (M-005): an incident without an Incident Owner
          gets an amber banner, with the take action for whoever may take it
          (`incident.take`). DO-001 needs the Owner for every status change,
          so this is also why the status control is absent (D-004). */}
      {mayTake ? (
        <section
          role="status"
          data-testid="incident-detail_owner-missing"
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 p-4 text-amber-900 dark:text-amber-100"
        >
          <div className="flex items-center gap-2 text-sm font-medium">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{t("detail.ownerMissing")}</span>
          </div>
          <Button
            size="sm"
            data-testid="incident-detail_assign-owner"
            onClick={focusAssignment}
          >
            {t("detail.take")}
          </Button>
        </section>
      ) : null}

      {/* Below-lg meta card: right after the header (single-column order),
          collapsible below md behind a min-h-11 toggle (M-005). The lg+
          sidebar renders its own copy. */}
      <Card data-testid="incident-detail_meta-inline" className="lg:hidden">
        <button
          type="button"
          data-testid="incident-detail_meta-toggle"
          aria-expanded={metaOpen}
          onClick={() => setMetaOpen((open) => !open)}
          className="flex min-h-11 w-full items-center justify-between px-6 py-3 text-sm font-medium text-muted-foreground md:hidden"
        >
          {t("detail.metaTitle")}
          <ChevronDown
            className={cn(
              "h-4 w-4 transition-transform",
              metaOpen && "rotate-180",
            )}
            aria-hidden="true"
          />
        </button>
        <CardHeader className="hidden md:block">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t("detail.metaTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent
          className={cn(
            "grid gap-4 sm:grid-cols-2",
            !metaOpen && "hidden md:grid",
          )}
        >
          {metaRows(false)}
        </CardContent>
      </Card>

      {/* Two-column desktop layout (M-005): work column left, context
          sidebar right; below lg everything stays one column. */}
      <div className="space-y-6 lg:grid lg:grid-cols-[1fr_324px] lg:items-start lg:gap-6 lg:space-y-0">
      <div className="space-y-6">

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t("detail.description")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p
            data-testid="incident-detail_description"
            className="whitespace-pre-wrap text-sm"
          >
            {incident.description}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="space-y-3">
          <CardTitle data-testid="incident-detail_timeline-count">
            {timelineItems.length === 1
              ? t("detail.timelineCountOne")
              : t("detail.timelineCountMany", { count: timelineItems.length })}
          </CardTitle>
          {/* Segmented Alle/Manuell/System filter (M-005), client-side only. */}
          <div
            role="group"
            data-testid="incident-detail_timeline-filters"
            aria-label={t("detail.timeline")}
            className="flex flex-wrap gap-1"
          >
            {TIMELINE_FILTERS.map((filter) => {
              const active = timelineFilter === filter;
              return (
                <button
                  key={filter}
                  type="button"
                  data-testid={`incident-detail_timeline-filter_${filter}`}
                  aria-pressed={active}
                  onClick={() => setTimelineFilter(filter)}
                  className={cn(
                    "h-8 rounded-md px-3 text-xs font-medium transition-colors",
                    active
                      ? "border border-primary-text bg-primary text-primary-foreground shadow"
                      : "border border-input bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                  )}
                >
                  {t(TIMELINE_FILTER_LABEL_KEY[filter])}
                </button>
              );
            })}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {timelineQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">
              {t("common.loading")}
            </p>
          ) : visibleTimelineItems.length === 0 ? (
            <p
              data-testid="incident-detail_timeline-empty"
              className="text-sm text-muted-foreground"
            >
              {t("detail.timelineEmpty")}
            </p>
          ) : (
            <ol data-testid="incident-detail_timeline" className="space-y-3">
              {visibleTimelineItems.map((event) => {
                const manual = isManualTimelineType(event.type);
                // System events render as a light one-liner (M-005); manual
                // entries keep their card shape.
                if (!manual) {
                  return (
                    <li
                      key={event.id}
                      data-testid={`incident-detail_timeline-entry_${event.id}`}
                      className="flex items-center gap-2 px-1 text-xs text-muted-foreground"
                    >
                      <span
                        className="h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/50"
                        aria-hidden="true"
                      />
                      <span>
                        {t(TIMELINE_TYPE_LABEL_KEY[event.type])}
                        {event.text ? `: ${timelineText(event, t)}` : ""} -{" "}
                        {userName(event.authorId, userById)} -{" "}
                        {formatDateTime(event.createdAt)}
                      </span>
                    </li>
                  );
                }
                return (
                  <li
                    key={event.id}
                    data-testid={`incident-detail_timeline-entry_${event.id}`}
                    className="rounded-md border p-3"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                      <Badge variant="secondary">
                        {t(TIMELINE_TYPE_LABEL_KEY[event.type])}
                      </Badge>
                      {/* K6: author monogram next to the manual-entry byline. */}
                      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                        <InitialsAvatar
                          name={userName(event.authorId, userById)}
                          size="sm"
                        />
                        {t("detail.by")} {userName(event.authorId, userById)} -{" "}
                        {formatDateTime(event.createdAt)}
                      </span>
                    </div>
                    {event.text ? (
                      <p className="mt-2 whitespace-pre-wrap text-sm">
                        {timelineText(event, t)}
                      </p>
                    ) : null}
                  </li>
                );
              })}
            </ol>
          )}

          {mayComment ? (
            <div
              data-testid="incident-detail_composer"
              className="space-y-3 border-t pt-4"
            >
              <p className="text-sm font-medium">{t("detail.addEntry")}</p>
              {/* Type picker as pill chips (M-005). Melder are fixed to
                  kommentar and get no picker (GR-003). */}
              {mayWriteEntry ? (
                <div
                  role="group"
                  data-testid="incident-detail_composer-types"
                  aria-label={t("detail.addEntry")}
                  className="flex flex-wrap gap-1.5"
                >
                  {MANUAL_TIMELINE_TYPES.map((type) => {
                    const active = composerType === type;
                    return (
                      <button
                        key={type}
                        type="button"
                        data-testid={`incident-detail_composer-type_${type}`}
                        aria-pressed={active}
                        onClick={() => setComposerType(type)}
                        className={cn(
                          "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                          active
                            ? "border-primary-text bg-primary text-primary-foreground"
                            : "border-input bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                        )}
                      >
                        {t(TIMELINE_TYPE_LABEL_KEY[type])}
                      </button>
                    );
                  })}
                </div>
              ) : null}
              <Textarea
                data-testid="incident-detail_composer-text"
                value={composerText}
                onChange={(event) => setComposerText(event.target.value)}
                rows={3}
                placeholder={t("detail.entryPlaceholder")}
              />
              <p className="text-xs text-muted-foreground">
                {t("detail.composerImmutable")}
              </p>
              <div className="flex justify-end">
                <Button
                  data-testid="incident-detail_composer-submit"
                  className="h-11 sm:h-9"
                  onClick={onAddEntry}
                  disabled={addEvent.isPending || composerText.trim().length === 0}
                >
                  {addEvent.isPending
                    ? t("common.loading")
                    : t("detail.addEntrySubmit")}
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>
      </div>

      {/* Context sidebar (lg+). CD-007 / LP-028: what one acts on comes first -
          Zuordnung (the Owner), the attachments (the upload), the Postmortem -
          and the read-only details last, compact, so no input sits below the
          fold of a long list of facts. */}
      <div className="space-y-6">

      {/* Zuordnung ([F-004]): who may act here is the incident's `can` - the
          Owner sets Owner and Bearbeiter (`incident.assign`), an Incident
          Owner takes an incident nobody owns (`incident.take`), the
          administration names a new Owner once the Owner is deactivated.
          People who hold neither Incident Owner nor Bearbeiter stay in the
          pickers, disabled, with the reason (AC-004). */}
      {mayTake || mayNameOwner ? (
        <Card
          ref={assignmentRef}
          tabIndex={-1}
          data-testid="incident-detail_assignment"
        >
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("detail.assignment")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {mayTake ? (
              <Button
                size="sm"
                data-testid="incident-detail_take"
                onClick={onTake}
                disabled={assignmentPending}
              >
                {t("detail.take")}
              </Button>
            ) : null}

            {mayNameOwner ? (
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t("detail.owner")}</label>
                <Select
                  value={incident.ownerId ?? OWNER_NONE}
                  onValueChange={onChangeOwner}
                  disabled={assignmentPending}
                >
                  <SelectTrigger
                    data-testid="incident-detail_owner-picker"
                    className="w-full max-w-72"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={OWNER_NONE}>
                      {t("detail.ownerNone")}
                    </SelectItem>
                    {incident.ownerId && ownerRetired ? (
                      <SelectItem value={incident.ownerId} disabled>
                        {userName(incident.ownerId, userById)}
                      </SelectItem>
                    ) : null}
                    {activeUsers(usersQuery.data).map((person) => (
                      <SelectItem
                        key={person.id}
                        value={person.id}
                        disabled={!isIncidentParty(person) && person.id !== incident.ownerId}
                      >
                        {isIncidentParty(person) || person.id === incident.ownerId
                          ? person.displayName
                          : t("incidentParty.notEligible", { name: person.displayName })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            {mayAssign ? (
              <div className="space-y-2">
                <p className="text-sm font-medium">{t("detail.assignees")}</p>
                {activeUsers(usersQuery.data).length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t("common.none")}
                  </p>
                ) : (
                  // CD-007: capped and scrollable - one checkbox per active user
                  // otherwise pushes the attachments (the upload) far below the
                  // fold as the team grows.
                  <div
                    data-testid="incident-detail_assignee-picker"
                    className="grid max-h-48 gap-2 overflow-y-auto rounded-md border p-2 sm:grid-cols-2 lg:grid-cols-1"
                  >
                    {activeUsers(usersQuery.data).map((person) => {
                      const checked = currentAssignees.includes(person.id);
                      // Someone already assigned stays removable even after
                      // losing the role; only a new naming is refused.
                      const eligible = isIncidentParty(person) || checked;
                      return (
                        <label
                          key={person.id}
                          className={cn(
                            "flex items-center gap-2 text-sm",
                            !eligible && "text-muted-foreground",
                          )}
                        >
                          <Checkbox
                            data-testid={`incident-detail_assignee_${person.id}`}
                            checked={checked}
                            disabled={!eligible}
                            onCheckedChange={(value) =>
                              onToggleAssignee(person.id, value === true)
                            }
                          />
                          {eligible
                            ? person.displayName
                            : t("incidentParty.notEligible", { name: person.displayName })}
                        </label>
                      );
                    })}
                  </div>
                )}
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    data-testid="incident-detail_assignees-save"
                    onClick={onSaveAssignees}
                    disabled={!assigneesDirty || assignmentPending}
                  >
                    {setAssignees.isPending
                      ? t("common.loading")
                      : t("detail.saveAssignees")}
                  </Button>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <AttachmentsCard incidentId={id} incident={incident} userById={userById} />

      {/* Linked Betriebslog entries and the link action ([F-013], AC-014). */}
      <IncidentOperationsLogCard incidentId={id} incident={incident} />

      <Card data-testid="incident-detail_postmortem">
        <CardHeader>
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t("postmortem.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {postmortemQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">
              {t("common.loading")}
            </p>
          ) : postmortemQuery.data ? (
            <>
              <p
                data-testid="incident-detail_postmortem-status"
                className="text-sm text-muted-foreground"
              >
                {isDraft(postmortemQuery.data)
                  ? t("postmortem.card.draft")
                  : missingMandatorySections(postmortemQuery.data).length === 0
                  ? t("postmortem.card.complete")
                  : t("postmortem.card.incomplete", {
                      count: missingMandatorySections(postmortemQuery.data)
                        .length,
                    })}
              </p>
              <Link
                to="/incidents/$id/postmortem"
                params={{ id }}
                data-testid="incident-detail_postmortem-open"
                className="inline-block text-sm font-medium underline underline-offset-4 hover:text-foreground"
              >
                {t("postmortem.card.open")}
              </Link>
            </>
          ) : canEditPostmortem(incident) &&
            (incident.status === "geloest" ||
              incident.status === "beobachtet") ? (
            <Button
              asChild
              size="sm"
              data-testid="incident-detail_postmortem-create"
            >
              <Link to="/incidents/$id/postmortem" params={{ id }}>
                {t("postmortem.card.create")}
              </Link>
            </Button>
          ) : (
            <p
              data-testid="incident-detail_postmortem-none"
              className="text-sm text-muted-foreground"
            >
              {t("postmortem.card.none")}
            </p>
          )}
        </CardContent>
      </Card>

      <Card data-testid="incident-detail_meta-sidebar" className="hidden lg:block">
        <CardHeader>
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t("detail.metaTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-2.5">{metaRows(true)}</CardContent>
      </Card>
      </div>
      </div>

      {/* Reopen dialog (F-018): mandatory Begruendung, Admin-Korrektur only
          for Admins. Reopening is not destructive - the confirm stays a normal
          primary action (UXDD M-005). */}
      <Dialog
        open={reopenOpen}
        onOpenChange={(open) => {
          if (!open) closeReopenDialog();
        }}
      >
        <DialogContent data-testid="incident-detail_reopen-dialog">
          <DialogHeader>
            <DialogTitle>{t("reopen.dialogTitle")}</DialogTitle>
            <DialogDescription>{t("reopen.dialogIntro")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="reopen-reason">{t("reopen.reason")}</Label>
            <Textarea
              id="reopen-reason"
              data-testid="incident-detail_reopen-reason"
              rows={3}
              value={reopenReason}
              onChange={(event) => setReopenReason(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {t("reopen.reasonHint", { chars: reopenReasonChars })}
            </p>
          </div>
          {isAdmin ? (
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                data-testid="incident-detail_reopen-admin-correction"
                checked={reopenAdminCorrection}
                onCheckedChange={(value) =>
                  setReopenAdminCorrection(value === true)
                }
              />
              {t("reopen.adminCorrection")}
            </label>
          ) : null}
          <DialogFooter>
            <Button
              variant="outline"
              data-testid="incident-detail_reopen-cancel"
              onClick={closeReopenDialog}
            >
              {t("common.cancel")}
            </Button>
            <Button
              data-testid="incident-detail_reopen-confirm"
              onClick={onConfirmReopen}
              disabled={
                reopenIncident.isPending ||
                reopenReasonChars < MIN_REOPEN_REASON_CHARS
              }
            >
              {reopenIncident.isPending
                ? t("reopen.submitting")
                : t("reopen.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set legal hold (GR-004): mandatory Begruendung. Setting the marker
          is not destructive - the confirm stays a normal primary action. */}
      <Dialog
        open={legalHoldSetOpen}
        onOpenChange={(open) => {
          if (!open) closeLegalHoldSetDialog();
        }}
      >
        <DialogContent data-testid="incident-detail_legal-hold-set-dialog">
          <DialogHeader>
            <DialogTitle>{t("legalHold.setTitle")}</DialogTitle>
            <DialogDescription>{t("legalHold.setIntro")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="legal-hold-reason">{t("legalHold.reason")}</Label>
            <Textarea
              id="legal-hold-reason"
              data-testid="incident-detail_legal-hold-reason"
              rows={3}
              value={legalHoldReason}
              onChange={(event) => setLegalHoldReason(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {t("legalHold.reasonHint", { chars: legalHoldReasonChars })}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              data-testid="incident-detail_legal-hold-set-cancel"
              onClick={closeLegalHoldSetDialog}
            >
              {t("common.cancel")}
            </Button>
            <Button
              data-testid="incident-detail_legal-hold-set-confirm"
              onClick={onConfirmSetLegalHold}
              disabled={
                setLegalHold.isPending ||
                legalHoldReasonChars < MIN_LEGAL_HOLD_REASON_CHARS
              }
            >
              {setLegalHold.isPending
                ? t("legalHold.setting")
                : t("legalHold.setConfirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear legal hold (GR-004): simple confirm showing the stored
          Begruendung. Non-destructive styling - the retention sweep resumes,
          nothing is deleted by this action itself. */}
      <Dialog open={legalHoldClearOpen} onOpenChange={setLegalHoldClearOpen}>
        <DialogContent data-testid="incident-detail_legal-hold-clear-dialog">
          <DialogHeader>
            <DialogTitle>{t("legalHold.clearTitle")}</DialogTitle>
            <DialogDescription>{t("legalHold.clearIntro")}</DialogDescription>
          </DialogHeader>
          {incident.legalHoldReason ? (
            <div className="space-y-1">
              <p className="text-sm font-medium">
                {t("legalHold.storedReason")}
              </p>
              <p
                data-testid="incident-detail_legal-hold-stored-reason"
                className="whitespace-pre-wrap text-sm text-muted-foreground"
              >
                {incident.legalHoldReason}
              </p>
            </div>
          ) : null}
          <DialogFooter>
            <Button
              variant="outline"
              data-testid="incident-detail_legal-hold-clear-cancel"
              onClick={() => setLegalHoldClearOpen(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              data-testid="incident-detail_legal-hold-clear-confirm"
              onClick={onConfirmClearLegalHold}
              disabled={clearLegalHold.isPending}
            >
              {clearLegalHold.isPending
                ? t("legalHold.clearing")
                : t("legalHold.clearConfirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetaRow({
  label,
  testid,
  children,
}: {
  label: string;
  testid: string;
  children: ReactNode;
}): JSX.Element {
  return (
    // Label and value on one line (CD-007): half the height of the stacked form.
    <div className="grid grid-cols-[7.5rem_1fr] items-baseline gap-x-3">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div data-testid={testid} className="min-w-0 break-words text-sm">
        {children}
      </div>
    </div>
  );
}

/** Structured detail skeleton (K6): head block, a timeline column of rows and
 *  a stack of sidebar blocks, matching the two-column detail layout. */
function DetailSkeleton(): JSX.Element {
  return (
    <div className="space-y-6">
      <div className="h-4 w-32 animate-pulse rounded bg-muted" />
      {/* Head block: number + status pill line, then the title. */}
      <div className="space-y-3 rounded-xl border p-6">
        <div className="flex items-center gap-3">
          <div className="h-4 w-24 animate-pulse rounded bg-muted" />
          <div className="h-5 w-20 animate-pulse rounded-full bg-muted" />
        </div>
        <div className="h-7 w-2/3 animate-pulse rounded bg-muted" />
      </div>
      <div className="space-y-6 lg:grid lg:grid-cols-[1fr_324px] lg:items-start lg:gap-6 lg:space-y-0">
        {/* Work column: description card + timeline rows. */}
        <div className="space-y-4 rounded-xl border p-6">
          <div className="h-4 w-28 animate-pulse rounded bg-muted" />
          <div className="h-16 w-full animate-pulse rounded bg-muted" />
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              className="h-12 w-full animate-pulse rounded-md bg-muted"
            />
          ))}
        </div>
        {/* Sidebar: meta / assignment / attachment blocks. */}
        <div className="space-y-6">
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              className="h-32 w-full animate-pulse rounded-xl bg-muted"
            />
          ))}
        </div>
      </div>
    </div>
  );
}
